import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import {
  getSubscriptionFeaturesLeftDetails,
  getSubscriptionTrialStatus as getSubscriptionTrialStatusApi,
} from "@/api/subscription";
import {
  SubscriptionOfferings,
  SubscriptionPlanTrialStatus,
  SubscriptionFeaturesLeft,
  CustomerInfo,
  SubscriptionPlan,
} from "@/types/subscription";
import purchases from "@/lib/purchases";
import { SUBSCRIPTION_PLANS } from "@/constants";
import { isSubscriptionBillingMigrationRequired } from "@/utils/subscription";

interface SubscriptionState {
  offerings: SubscriptionOfferings | null;
  customerInfo: CustomerInfo | null;
  subscription: SubscriptionPlan;
  currentPlan: string | null;
  upcomingPlan: string | null;
  purchasedAt: string;
  store: string | null;
  identifier: string | null;
  productIdentifier: string | null;
  error: string | null;
  isSubscriptionCancelled: boolean;
  isLoading: boolean;
  /**
   * Whether the backend subscription has arrived. Until it has, `subscription` holds empty
   * defaults that read as "no plan", so gates must wait instead of acting on them.
   */
  subscriptionLoad: "pending" | "loaded" | "failed";
}

type SubscriptionPlanConfig = (typeof SUBSCRIPTION_PLANS)[number];

const normalizePlanName = (planName: string | null | undefined) =>
  planName
    ?.trim()
    .toLowerCase()
    .replace(/^go2work\s+/i, "")
    .replace(/\s+plan$/i, "") ?? "";

const findSubscriptionPlan = ({
  key,
  planName,
}: {
  key?: string | null;
  planName?: string | null;
}): SubscriptionPlanConfig | null => {
  const normalizedKey = key?.trim().toLowerCase();
  if (normalizedKey) {
    const planByKey = SUBSCRIPTION_PLANS.find((plan) => plan.key.toLowerCase() === normalizedKey);
    if (planByKey) {
      return planByKey;
    }
  }

  const normalizedPlanName = normalizePlanName(planName);
  if (!normalizedPlanName) {
    return null;
  }

  return (
    SUBSCRIPTION_PLANS.find((plan) => {
      const normalizedTitle = normalizePlanName(plan.title);
      return normalizedTitle === normalizedPlanName || plan.key === normalizedPlanName;
    }) ?? null
  );
};

const resetCurrentPlanState = (state: SubscriptionState) => {
  state.currentPlan = null;
  state.purchasedAt = "";
  state.store = null;
  state.identifier = null;
  state.productIdentifier = null;
  state.isSubscriptionCancelled = false;
};

const applyCurrentPlanState = ({
  state,
  plan,
  store,
  purchasedAt = "",
  productIdentifier = null,
  isSubscriptionCancelled = false,
}: {
  state: SubscriptionState;
  plan: SubscriptionPlanConfig;
  store: string | null | undefined;
  purchasedAt?: string;
  productIdentifier?: string | null;
  isSubscriptionCancelled?: boolean;
}) => {
  state.currentPlan = plan.title;
  state.purchasedAt = purchasedAt;
  state.store = store ?? null;
  state.identifier = plan.key;
  state.productIdentifier = productIdentifier;
  state.isSubscriptionCancelled = isSubscriptionCancelled;
};

const syncPlanFromSubscription = (state: SubscriptionState, subscription: SubscriptionPlan) => {
  const plan = findSubscriptionPlan({
    key: subscription.entitlement_id,
    planName: subscription.plan_name,
  });
  if (!plan) {
    resetCurrentPlanState(state);
    state.store = subscription.store ?? null;
    state.isSubscriptionCancelled = subscription.is_cancelled;
    return;
  }

  applyCurrentPlanState({
    state,
    plan,
    store: subscription.store,
    isSubscriptionCancelled: subscription.is_cancelled,
  });
};

const syncPlanFromCustomerInfo = (state: SubscriptionState, customerInfo: CustomerInfo) => {
  if (!customerInfo?.entitlements) return;

  const subscriptionPlan = findSubscriptionPlan({
    key: state.identifier,
    planName: state.subscription.plan_name,
  });

  if (subscriptionPlan) {
    const matchedEntitlement = customerInfo.entitlements.all?.[subscriptionPlan.key];

    applyCurrentPlanState({
      state,
      plan: subscriptionPlan,
      store: matchedEntitlement?.store ?? state.subscription.store,
      purchasedAt: matchedEntitlement?.latestPurchaseDate ?? "",
      productIdentifier: matchedEntitlement?.productIdentifier ?? null,
      isSubscriptionCancelled:
        matchedEntitlement?.unsubscribeDetectedAt != null || state.subscription.is_cancelled,
    });
    return;
  }

  for (const [key, value] of Object.entries(customerInfo.entitlements.active ?? {})) {
    const activePlan = findSubscriptionPlan({ key });
    if (!activePlan) {
      continue;
    }

    applyCurrentPlanState({
      state,
      plan: activePlan,
      store: value.store,
      purchasedAt: value.latestPurchaseDate,
      productIdentifier: value.productIdentifier,
      isSubscriptionCancelled: value.unsubscribeDetectedAt !== null,
    });
    return;
  }

  resetCurrentPlanState(state);
};

const syncPlanFromCustomerInfoIfAllowed = (
  state: SubscriptionState,
  customerInfo: CustomerInfo,
) => {
  // CustomerInfo can resolve before the backend subscription payload on startup.
  // We still sync it for normal flows because setSubscriptionData later re-applies
  // the authoritative backend state once it arrives.
  if (isSubscriptionBillingMigrationRequired(state.subscription)) {
    return;
  }

  syncPlanFromCustomerInfo(state, customerInfo);
};

const initialState: SubscriptionState = {
  offerings: null,
  customerInfo: null,
  currentPlan: null,
  upcomingPlan: null,
  purchasedAt: "",
  store: null,
  identifier: null,
  productIdentifier: null,
  error: null,
  isSubscriptionCancelled: false,
  isLoading: false,
  subscriptionLoad: "pending",
  subscription: {
    customer_id: "",
    subscription_id: "",
    entitlement_id: null,
    transaction_id: null,
    plan_name: "",
    store: "",
    starts_at_ms: 0,
    expiration_at_ms: 0,
    display_renewal_at_ms: null,
    msLeft: 0,
    daysLeft: 0,
    features: {
      job_post: 0,
      shortlist: 0,
    },
    product_change_details: null,
    have_trial: false,
    is_trialing: false,
    is_cancelled: false,
    was_trial_cancelled: false,
    is_active: false,
    has_stripe_subscription: false,
    requires_billing_migration: false,
    billing_migration_reason: null,
    billing_migration_expires_at_ms: null,
    featuresLeft: {
      apply_request_left: 0,
      job_post_left: 0,
    },
  },
};

export const getSubscriptionFeaturesLeft = createAsyncThunk(
  "purchases/subscription/getSubscriptionFeaturesLeft",
  async (organizationId: string) => {
    try {
      const response = await getSubscriptionFeaturesLeftDetails(organizationId);
      return response;
    } catch (error) {
      throw error;
    }
  },
);

export const getSubscriptionTrialStatus = createAsyncThunk(
  "purchases/subscription/getSubscriptionTrialStatus",
  async (organizationId: string) => {
    try {
      const response = await getSubscriptionTrialStatusApi(organizationId);
      return response;
    } catch (error) {
      throw error;
    }
  },
);

export const getPurchasesOfferings = createAsyncThunk(
  "purchases/subscription/getPurchasesOfferings",
  async () => {
    try {
      return await purchases.getOfferings();
    } catch (error) {
      throw error;
    }
  },
);

export const getPurchasesCustomerInfo = createAsyncThunk(
  "purchases/subscription/getPurchasesCustomerInfo",
  async () => {
    try {
      return await purchases.getCustomerInfo();
    } catch (error) {
      throw error;
    }
  },
);

const purchasesSlice = createSlice({
  name: "purchases",
  initialState,
  reducers: {
    setSubscriptionData: (state, action: PayloadAction<SubscriptionPlan>) => {
      // The backend subscription payload is the authoritative current-plan source.
      state.subscription = action.payload;
      state.subscriptionLoad = "loaded";
      syncPlanFromSubscription(state, action.payload);
    },
    setPurchasesOfferings: (state, action: PayloadAction<SubscriptionOfferings>) => {
      state.offerings = action.payload;
    },
    setPurchasesCustomerInfo: (state, action: PayloadAction<CustomerInfo | undefined>) => {
      if (!action.payload) return;
      state.customerInfo = action.payload;
      syncPlanFromCustomerInfoIfAllowed(state, action.payload);
    },
    setPurchasesLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUpcomingPlan: (state, action: PayloadAction<SubscriptionPlan | null>) => {
      state.upcomingPlan = action.payload?.product_change_details?.new_entitlement_id ?? null;
    },
    /** The subscription request failed with nothing loaded before it. */
    setSubscriptionLoadFailed: (state) => {
      if (state.subscriptionLoad !== "loaded") state.subscriptionLoad = "failed";
    },
    clearSubscriptions: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionFeaturesLeft.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getSubscriptionFeaturesLeft.fulfilled,
        (state, action: PayloadAction<SubscriptionFeaturesLeft>) => {
          state.isLoading = false;
          state.subscription.featuresLeft = action.payload;
        },
      )
      .addCase(getSubscriptionFeaturesLeft.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch subscription features left.";
      })
      .addCase(getSubscriptionTrialStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getSubscriptionTrialStatus.fulfilled,
        (state, action: PayloadAction<SubscriptionPlanTrialStatus>) => {
          state.isLoading = false;
          state.subscription.msLeft = action.payload.ms_left;
          state.subscription.is_trialing = action.payload.on_trial;
        },
      )
      .addCase(getSubscriptionTrialStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch subscription trial status.";
      })
      .addCase(getPurchasesOfferings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPurchasesOfferings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.offerings = action.payload;
      })
      .addCase(getPurchasesOfferings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch purchases offerings data.";
      })
      .addCase(getPurchasesCustomerInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPurchasesCustomerInfo.fulfilled, (state, action: PayloadAction<CustomerInfo>) => {
        state.isLoading = false;
        state.customerInfo = action.payload;
        syncPlanFromCustomerInfoIfAllowed(state, action.payload);
      })
      .addCase(getPurchasesCustomerInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Failed to fetch purchases customer info and current plan.";
      });
  },
});

export const {
  setPurchasesOfferings,
  setPurchasesCustomerInfo,
  setSubscriptionData,
  setPurchasesLoading,
  clearSubscriptions,
  setUpcomingPlan,
  setSubscriptionLoadFailed,
} = purchasesSlice.actions;

export default purchasesSlice.reducer;
